/**
 * Ein Raum der Band-Bibel: die Notizen darin.
 *
 * Eine Notiz ist keine Textdatei, sondern eine eigene freie Flaeche. Die
 * Liste zeigt deshalb, was darauf liegt: Objekte, Mindmap, Handschrift.
 */
import type { View } from '../app.ts';
import { navigate, refresh } from '../app.ts';
import type { Board, Room } from '../types.ts';
import { activeProject } from '../db/active.ts';
import {
  boardSurfaceId,
  boardSummary,
  boardTitle,
  childRooms,
  ensureRooms,
  isSystemRoom,
  deleteBoard,
  emptyBoard,
  getDrawing,
  getRoom,
  listBoards,
  saveBoard,
  saveRoom,
} from '../db/repo.ts';
import { button, h } from '../ui/dom.ts';
import { icon } from '../ui/icons.ts';
import { toast } from '../ui/toast.ts';
import { noteDate } from '../ui/format.ts';
import { askText } from '../ui/ask.ts';

export async function renderBibleRoom(params: Record<string, string>): Promise<View> {
  const project = await activeProject();
  if (!project) {
    navigate('#/projects', true);
    return { title: 'Projekte', body: h('div'), drawing: null };
  }

  const projectId = project.id;
  const room = await getRoom(params.id as string);
  if (!room) {
    return {
      title: 'Raum nicht gefunden',
      back: '#/bible',
      body: h('div', { class: 'empty', text: 'Dieser Raum wurde geloescht.' }),
    };
  }
  const found: Room = room;

  // Eigene Raeume in dieser Kategorie – sie stehen ueber der Liste.
  const allRooms = await ensureRooms(projectId);
  const children = childRooms(allRooms, found.id);

  const boards = await listBoards(found.id);
  // Fuer jede Notiz einmal nachsehen, ob Handschrift darauf liegt.
  const inked = await Promise.all(
    boards.map(async (b) => (await getDrawing(boardSurfaceId(b.id))).strokes.length > 0),
  );

  async function create(): Promise<void> {
    const board = await saveBoard(emptyBoard(projectId, found.id));
    navigate(`#/bible/board/${board.id}`);
  }

  async function rename(board: Board): Promise<void> {
    const title = await askText({
      title: 'Notiz umbenennen',
      label: 'Titel der Notiz',
      value: boardTitle(board),
      confirm: 'Speichern',
      allowEmpty: true,
    });
    if (title === null) return;
    await saveBoard({ ...board, title });
    refresh();
  }

  async function remove(board: Board): Promise<void> {
    if (!confirm(`"${boardTitle(board) || 'Neue Fläche'}" löschen?`)) return;
    await deleteBoard(board.id);
    toast('Notiz gelöscht');
    refresh();
  }

  const listEl = h('ul', { class: 'list' });

  if (boards.length === 0) {
    listEl.append(
      h(
        'li',
        { class: 'empty' },
        h('p', { class: 'empty__title', text: 'Noch nichts hier' }),
        h('p', { text: 'Eine Fläche für Skizzen, Ablaufpläne, Mindmaps – was du brauchst.' }),
        h(
          'div',
          { class: 'btn-row', style: { justifyContent: 'center', marginTop: '16px' } },
          button('Fläche anlegen', () => void create(), { class: 'btn btn--primary' }),
        ),
      ),
    );
  } else {
    boards.forEach((board, index) => {
      listEl.append(
        h(
          'li',
          { class: 'list-item' },
          h(
            'button',
            {
              class: 'list-item__main',
              attrs: { type: 'button' },
              on: { click: () => navigate(`#/bible/board/${board.id}`) },
            },
            h('span', { class: 'list-item__title', text: boardTitle(board) || 'Neue Fläche' }),
            h('span', {
              class: 'list-item__meta',
              text: `${noteDate(board.updatedAt)} · ${boardSummary(board, inked[index] === true)}`,
            }),
          ),
          h(
            'div',
            { class: 'list-item__actions' },
            button('Umbenennen', () => void rename(board), {
              class: 'btn btn--ghost btn--small',
              title: 'Titel ändern',
            }),
            button('Löschen', () => void remove(board), {
              class: 'btn btn--ghost btn--small',
              title: 'Notiz löschen',
            }),
          ),
        ),
      );
    });
  }

  /** Zeile mit den Unterraeumen, falls es welche gibt. */
  const childBar =
    children.length === 0
      ? null
      : h(
          'div',
          { class: 'room-crumbs' },
          h('span', { class: 'room-crumbs__label', text: 'Räume:' }),
          ...children.map((child) =>
            button(child.name, () => navigate(`#/bible/room/${child.id}`), {
              class: 'room__chip-main',
              title: `„${child.name}“ öffnen`,
            }),
          ),
        );

  const body = h(
    'div',
    {},
    childBar,
    listEl,
    boards.length > 0
      ? h('button', {
          class: 'fab',
          text: '+ Fläche',
          attrs: { type: 'button' },
          on: { click: () => void create() },
        })
      : null,
  );

  return {
    title: found.name || 'Raum',
    subtitle: `Band-Bibel · ${project.name || 'Ohne Namen'}`,
    back: found.parentId ? `#/bible/room/${found.parentId}` : '#/bible',
    actions: isSystemRoom(found)
      ? // Feste Kategorien lassen sich nicht umbenennen – dann gibt es den
        // Knopf gar nicht erst, statt ihn anzuzeigen und abzulehnen.
        [h('span', { class: 'topbar__sub', text: 'Feste Kategorie' })]
      : [
          h(
            'button',
            {
              class: 'btn btn--icon',
              attrs: { type: 'button', title: 'Raum umbenennen', 'aria-label': 'Raum umbenennen' },
              on: {
                click: async () => {
                  const name = await askText({
                    title: 'Raum umbenennen',
                    label: 'Name des Raums',
                    value: found.name,
                    confirm: 'Speichern',
                  });
                  if (name === null) return;
                  await saveRoom({ ...found, name });
                  refresh();
                },
              },
            },
            icon('pencil'),
          ),
        ],
    body,
  };
}
